import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { resolveImg } from "@/lib/api";

export default function Works() {
  const { works } = useData();
  const [filter, setFilter] = useState("Tutti");

  const categories = useMemo(() => {
    const set = new Set(works.map((w) => w.categoria));
    return ["Tutti", ...Array.from(set)];
  }, [works]);

  const filtered = filter === "Tutti" ? works : works.filter((w) => w.categoria === filter);

  return (
    <>
      <Seo title="I nostri lavori" description="Portfolio dei lavori Animaverde: manutenzione giardini, potature, abbattimenti, trattamenti fitosanitari, nuovi giardini e rifacimenti in provincia di Varese." path="/lavori" />
      <PageHero
        overline="Portfolio"
        title="I nostri lavori"
        subtitle="Una selezione dei nostri interventi. I contenuti attuali sono dimostrativi e verranno sostituiti con fotografie reali dei lavori realizzati."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Lavori</span></>}
      />

      <section className="bg-av-black py-14 sm:py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="flex flex-wrap gap-2.5 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`works-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filter === c ? "bg-av-bright text-av-black" : "border border-white/20 text-av-white/80 hover:border-av-lime hover:text-av-lime"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((w) => (
                <motion.article
                  key={w.id}
                  layout
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`work-card-${w.id}`}
                  className="group"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/3]">
                    <img src={resolveImg(w.immagine)} alt={w.alt || w.titolo} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-3 left-3 rounded-full bg-av-black/70 backdrop-blur px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-av-lime">{w.categoria}</span>
                    {w.placeholder && <span className="absolute top-3 right-3 rounded-full bg-av-bright/90 px-2.5 py-1 text-[10px] font-semibold uppercase text-av-black">Demo</span>}
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center gap-1.5 text-xs text-av-gray"><MapPin size={12} /> {w.comune} · {w.data}</div>
                    <h3 className="mt-1.5 font-display text-xl text-av-white">{w.titolo}</h3>
                    <p className="mt-2 text-sm text-av-gray line-clamp-2">{w.descrizione}</p>
                    <Link to="/contatti" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-av-lime opacity-90 hover:gap-3 transition-all">
                      Richiedi un intervento simile <ArrowUpRight size={14} />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </>
  );
}
